import { Body1Strong, Button } from '@fluentui/react-components';
import { EItemScreenMode, TResolvedItemsMap } from '@modules/items';
import { useImpactSourceStore } from '@modules/impacts';
import {
  EHeadingSelectionLabel,
  EHeadingsSelectionMode,
  isTDocument,
  SELECT_HEADING_DIALOG_MODE_LOCKED_MESSAGE,
  SelectHeadingDialog,
  TDocument,
  TDraft,
  TSelectHeadingDialogState,
  TVersion,
} from '@modules/documents';
import { TFile, TFileVersion } from '@modules/files';
import { useEffect, useState } from 'react';
import ImpactCard from './ImpactCard';
import useImpacts from '../hooks/useImpacts';
import ImpactAnalysisPanel from './ImpactAnalysisPanel';
import ChangingSections from './ChangingSections';

type TProps = {
  item: TDocument | TFile;
  resolvedItems: TResolvedItemsMap;
  version: TVersion | TDraft | TFileVersion;
};

const PreChangeImpactAnalysis = ({ item, resolvedItems, version }: TProps) => {
  const { selectedHeadingIds, autoDisabledIds, toggleHeading, setSelection, clearSelection } = useImpactSourceStore();
  const isDoc = isTDocument(item);

  const docVersion = isDoc ? (version as TVersion | TDraft) : undefined;

  const impacts = useImpacts(
    item,
    docVersion?.headings,
    resolvedItems,
    Array.from(selectedHeadingIds),
    EItemScreenMode.PRE_CHANGE_IMPACT_ANALYSIS
  );

  const [selectHeadingDialogState, setSelectHeadingDialogState] = useState<TSelectHeadingDialogState | undefined>(
    undefined
  );

  const handleOpenHeadingSelectionDialog = async () => {
    const availableHeadings = docVersion?.headings || [];

    const isHeadingsPresent = availableHeadings.length > 0;
    const impactedHeadings = availableHeadings.filter((h) => selectedHeadingIds.has(h.id));
    const mode =
      impactedHeadings.length || isHeadingsPresent ? EHeadingsSelectionMode.SPECIFIC : EHeadingsSelectionMode.ALL;
    const lockedModeMessage = isHeadingsPresent ? undefined : SELECT_HEADING_DIALOG_MODE_LOCKED_MESSAGE.NO_HEADINGS;

    const initialSelectedIds = impactedHeadings.map((h) => h.id);
    const initialAutoDisabledIds = initialSelectedIds.filter((id) => autoDisabledIds.has(id));

    setSelectHeadingDialogState({
      isOpen: true,
      documentId: item._id,
      // We only open the dialog if the item is a document. Hence explicit type assertion.
      version: docVersion as TVersion | TDraft,
      mode,
      modeLocked: {
        isModeLocked: !isHeadingsPresent, // Lock the mode to "ALL" if there are no headings
        lockedModeMessage,
      },
      initialSelectedIds,
      initialAutoDisabledIds,
      onSelect: async (_, newSelectedIds, newAutoDisabledIds) => {
        setSelection(newSelectedIds, newAutoDisabledIds);
        setSelectHeadingDialogState((prev) => prev && { ...prev, isOpen: false });
      },
      enableAutoChildrenSelection: true, // Auto select child headings
    });
  };

  useEffect(() => {
    clearSelection();
  }, []);

  return (
    <>
      <SelectHeadingDialog
        state={selectHeadingDialogState}
        setState={setSelectHeadingDialogState}
        multiSelect={true}
        label={EHeadingSelectionLabel.ANALYZE}
      />
      <ImpactAnalysisPanel title="Pre-change impact analysis">
        <div className="flex flex-col gap-6">
          {isDoc && (
            <div>
              <p className="mb-2">
                <Body1Strong>Step 1:</Body1Strong> Select changing sections:
              </p>
              <ChangingSections
                version={version as TVersion}
                changedSectionIds={Array.from(selectedHeadingIds)}
                isEntireDocumentAsDefault
                onRemove={(id) => toggleHeading(id, docVersion as TVersion | TDraft)}
              />
              <div className="mt-2">
                <Button appearance="primary" size="small" onClick={handleOpenHeadingSelectionDialog} disabled={!isDoc}>
                  Select
                </Button>
              </div>
            </div>
          )}
          <div>
            <p className="mb-2">
              <Body1Strong>Step {isDoc ? 2 : 1}:</Body1Strong> See what files may be impacted:
            </p>

            {/* Enabled later:
              <Menu>
                <MenuTrigger disableButtonEnhancement>
                  <MenuButton>Show 1st degree impacts</MenuButton>
                </MenuTrigger>

                <MenuPopover>
                  <MenuList>
                    <MenuItem>1st degree impacts</MenuItem>
                    <MenuItem disabled>2nd degree impacts</MenuItem>
                    <MenuItem disabled>3rd degree impacts</MenuItem>
                  </MenuList>
                </MenuPopover>
              </Menu>
            */}

            {impacts.length === 0 ? (
              <p>No impacts found.</p>
            ) : (
              <div className="flex flex-col gap-2">
                {impacts.map((impact, idx) => (
                  <ImpactCard {...impact} key={idx} />
                ))}
              </div>
            )}
          </div>
        </div>
      </ImpactAnalysisPanel>
    </>
  );
};

export default PreChangeImpactAnalysis;
